/**
 * ReportVersioningPage — `/reporting/reports/:id/versions`.
 *
 * Lists prior runs for the same `reportKey` so the operator can compare.
 * Wired against:
 *   GET /v1/reporting/runs?reportKey=…
 *   GET /v1/reporting/runs/:a/diff/:b   (real diff endpoint, B-slice 5+E2E)
 */
import { type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  diffReportRuns,
  getReportRun,
  listReportRuns,
  type DiffResponseWire,
  type ReportRunWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function ReportVersioningPage(): JSX.Element {
  const { id } = useParams();
  const runId = id ?? '';
  const [diffPick, setDiffPick] = useState<string>('');

  const baseRun = useQuery<ReportRunWire, Error>({
    queryKey: ['reporting', 'run', runId],
    queryFn: () => getReportRun(runId),
    enabled: runId.length > 0,
    retry: false,
  });

  const reportKey = baseRun.data?.reportKey;
  const allRuns = useQuery<readonly ReportRunWire[], Error>({
    queryKey: ['reporting', 'runs', reportKey ?? ''],
    queryFn: () => listReportRuns(reportKey),
    enabled: reportKey !== undefined,
    retry: false,
  });

  const diff = useMutation<DiffResponseWire, Error>({
    mutationFn: () => diffReportRuns(runId, diffPick),
  });

  const denied =
    baseRun.isError && baseRun.error instanceof ApiError &&
    (baseRun.error.status === 401 || baseRun.error.status === 403);

  const otherRuns = (allRuns.data ?? []).filter((r) => r.id !== runId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Report versions"
        description={runId.length > 0 ? `History for run ${runId}` : 'No run id supplied.'}
        actions={
          <Link
            to={`/reporting/reports/${encodeURIComponent(runId)}`}
            className="px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
          >
            ← Back to viewer
          </Link>
        }
      />

      {runId.length === 0 ? (
        <p className="text-sm text-muted">Append a run id.</p>
      ) : baseRun.isPending ? (
        <p className="text-sm text-muted">Loading run…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/getRun</code> required.
        </DeferredNotice>
      ) : baseRun.isError ? (
        <DeferredNotice title="Run not found">
          {baseRun.error.message}
        </DeferredNotice>
      ) : allRuns.isPending ? (
        <p className="text-sm text-muted">Loading versions…</p>
      ) : allRuns.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {allRuns.error.message}
        </DeferredNotice>
      ) : (
        <>
          {/* Diff picker */}
          <div className="bg-white border border-border rounded-xl p-5" data-testid="diff-picker">
            <h3 className="font-semibold text-gray-900 mb-3">Compare with another run</h3>
            <div className="flex items-center gap-3">
              <select
                value={diffPick}
                onChange={(e) => setDiffPick(e.target.value)}
                aria-label="Comparison run"
                data-testid="diff-pick-select"
                className="px-3 py-2 text-sm border border-border rounded-lg flex-1"
              >
                <option value="">— pick a run —</option>
                {otherRuns.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id.slice(0, 8)}… · {r.startedAt}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => diff.mutate()}
                disabled={diffPick.length === 0 || diff.isPending}
                data-testid="diff-run-btn"
                className="px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
              >
                {diff.isPending ? 'Diffing…' : 'Diff'}
              </button>
            </div>
            {diff.isError && (
              <p className="mt-3 text-xs text-red-600">{diff.error.message}</p>
            )}
            {diff.data && (
              <div className="mt-4" data-testid="diff-result">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  {diff.data.entries.length} field{diff.data.entries.length === 1 ? '' : 's'} differ
                </p>
                {diff.data.entries.length === 0 ? (
                  <p className="text-xs text-muted">No differences — runs are identical.</p>
                ) : (
                  <table className="w-full text-xs" data-testid="diff-table">
                    <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                      <tr>
                        <th className="text-left px-3 py-2">Path</th>
                        <th className="text-left px-3 py-2">A</th>
                        <th className="text-left px-3 py-2">B</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {diff.data.entries.map((e, i) => (
                        <tr key={`${e.path}-${i}`}>
                          <td className="px-3 py-2 font-mono">{e.path}</td>
                          <td className="px-3 py-2 font-mono text-red-600">{JSON.stringify(e.a)}</td>
                          <td className="px-3 py-2 font-mono text-nipost-green">{JSON.stringify(e.b)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-nipost-green" />
              <h3 className="font-semibold text-gray-900">
                {(allRuns.data?.length ?? 0).toLocaleString('en-US')} versions for {baseRun.data.reportKey}
              </h3>
            </div>
            <ul className="divide-y divide-border">
              {(allRuns.data ?? []).map((r) => (
                <li key={r.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-mono">{r.id}</p>
                    <p className="text-xs text-muted">{r.startedAt}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={r.status} />
                    <Link
                      to={`/reporting/reports/${encodeURIComponent(r.id)}`}
                      className="text-xs font-semibold text-nipost-green hover:underline"
                    >
                      Open →
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
